import {
	DEFAULT_LANGUAGE_TAG,
	PSEUDO_LANGUAGE_TAG,
	SUPPORTED_LANGUAGES,
} from '@radius/ui-utils';
import type { Resource } from 'i18next';
import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import Pseudo from 'i18next-pseudo';
import { initReactI18next } from 'react-i18next';

import shellEnUs from '../public/locales/en-US/external_shell.json';

import {
	i18nDebugError,
	i18nDebugLog,
	isI18nCimodeNsAppended,
	isI18nDebugActive,
	isI18nPseudoWrapped,
} from './Auxillary/i18nDebug';
import { SHELL_I18N_NAMESPACE } from './constants/i18n';

const FALLBACK_LANGUAGE = DEFAULT_LANGUAGE_TAG;

export const I18N_LANGUAGE_STORAGE_KEY = 'i18n-language';

/**
 * Map an arbitrary BCP 47 tag (e.g. `en-ZA`) to the closest entry in
 * `SUPPORTED_LANGUAGES`. Tries exact match first, then matches on the
 * primary language subtag (`en-ZA` -> `en-US`), then falls back.
 */
const resolveSupportedLanguage = (tag: string): string => {
	if (SUPPORTED_LANGUAGES.includes(tag)) return tag;

	const primarySubtag = tag.split('-')[0].toLowerCase();
	const match = SUPPORTED_LANGUAGES.find(
		(supported) => supported.split('-')[0].toLowerCase() === primarySubtag,
	);

	return match ?? FALLBACK_LANGUAGE;
};

// Bundle only the fallback (and pseudo, which wraps fallback content) so
// the shell can render synchronously on first paint without inflating the
// main bundle with every translation. Non-fallback languages are fetched
// by the HTTP backend; `fallbackLng` covers the brief gap.
const bundledResources: Resource = {
	[FALLBACK_LANGUAGE]: { [SHELL_I18N_NAMESPACE]: shellEnUs },
	[PSEUDO_LANGUAGE_TAG]: { [SHELL_I18N_NAMESPACE]: shellEnUs },
};

/**
 * Language resolution order:
 * 1. Stored preference in localStorage — set either by the header language
 *    picker (an explicit end-user choice) or the dev panel override
 * 2. Browser language
 * 3. en-US
 *
 * Only reached when language configuration is unlocked (see
 * `isLanguageConfigurationLocked`); when locked the shell forces en-US and
 * never calls this.
 *
 * The detected tag is normalised to a supported language so i18next never
 * receives an unsupported tag (which would trigger `supportedLngs` warnings
 * and leave `i18n.languages` empty).
 */
const detectLanguage = (): string => {
	const storedLanguage = localStorage.getItem(I18N_LANGUAGE_STORAGE_KEY);

	if (storedLanguage) {
		const resolved = resolveSupportedLanguage(storedLanguage);

		i18nDebugLog(
			'detected language:',
			storedLanguage,
			'-> resolved to supported:',
			resolved,
		);

		return resolved;
	}

	// Walk the browser's full preference list before falling back, so a
	// user whose first preference is unsupported still gets their second
	// choice instead of jumping straight to en-US.
	const preferences =
		navigator.languages?.length > 0
			? Array.from(navigator.languages)
			: [navigator.language || FALLBACK_LANGUAGE];

	const supportedMatch = preferences.find(
		(candidate) =>
			candidate === FALLBACK_LANGUAGE ||
			resolveSupportedLanguage(candidate) !== FALLBACK_LANGUAGE,
	);

	const resolved = supportedMatch
		? resolveSupportedLanguage(supportedMatch)
		: FALLBACK_LANGUAGE;

	i18nDebugLog(
		'detected language:',
		supportedMatch ?? '(none)',
		'-> resolved to supported:',
		resolved,
	);

	return resolved;
};

/**
 * Returns `true` when language configuration is gated off — i.e. the
 * `enableLanguageConfiguration` LD flag is `false` AND the i18n debug
 * tooling is not active. In this state the shell is locked to `en-US`.
 *
 * The debug override (`i18n-debug=true` in localStorage) always wins so
 * QA / engineering can validate non-English locales regardless of the
 * production rollout state of the flag.
 */
const isLanguageConfigurationLocked = (
	enableLanguageConfiguration: boolean,
): boolean => !enableLanguageConfiguration && !isI18nDebugActive;

interface InitI18nOptions {
	/**
	 * Value of the `enableLanguageConfiguration` LaunchDarkly flag.
	 * Defaults to `true` so existing callers / tests keep the prior
	 * multi-language behaviour; `index.tsx` passes the actual flag
	 * value from LD.
	 */
	enableLanguageConfiguration?: boolean;
}

const initI18n = async ({
	enableLanguageConfiguration = true,
}: InitI18nOptions = {}): Promise<typeof i18n> => {
	i18nDebugLog('initializing i18n...');

	const languageLocked = isLanguageConfigurationLocked(
		enableLanguageConfiguration,
	);
	const lng = languageLocked ? FALLBACK_LANGUAGE : detectLanguage();
	const supportedLngs = languageLocked
		? [FALLBACK_LANGUAGE]
		: [...SUPPORTED_LANGUAGES];

	i18nDebugLog(
		'enableLanguageConfiguration:',
		enableLanguageConfiguration,
		'-> languageLocked:',
		languageLocked,
	);

	const pseudo = new Pseudo({
		enabled: true,
		languageToPseudo: PSEUDO_LANGUAGE_TAG,
		wrapped: isI18nPseudoWrapped,
	});

	// Skip pseudo-wrapping when the resolved value equals the key — that
	// means the translation is missing and i18next has fallen back to the
	// raw key. Wrapping it in pseudo characters obscures the typo/missing
	// key and makes it harder to spot during QA.
	const pseudoProcessor = pseudo as unknown as {
		process: (
			value: string,
			key: string | Array<string>,
			options: unknown,
			translator: unknown,
		) => string;
	};
	const originalProcess = pseudoProcessor.process.bind(pseudoProcessor);

	pseudoProcessor.process = (value, key, options, translator) => {
		const keys = Array.isArray(key) ? key : [key];

		if (keys.includes(value)) {
			// eslint-disable-next-line no-console -- intentional dev-only warning for missing i18n keys
			console.warn('[i18n] missing key:', value);

			return `🔴 ${value} 🔴`;
		}

		return originalProcess(value, key, options, translator);
	};

	try {
		await i18n
			.use(HttpBackend)
			.use(pseudo)
			.use(initReactI18next)
			.init({
				lng,
				fallbackLng: FALLBACK_LANGUAGE,
				supportedLngs,
				load: 'currentOnly',
				defaultNS: SHELL_I18N_NAMESPACE,
				ns: [SHELL_I18N_NAMESPACE],
				debug: isI18nDebugActive,
				keySeparator: false,
				nsSeparator: false,
				// In debug, render missing keys as the key itself so typos are
				// visible. In prod, fall back to empty string to avoid leaking
				// raw keys to end users.
				returnEmptyString: !isI18nDebugActive,
				appendNamespaceToCIMode: isI18nCimodeNsAppended,
				postProcess: ['pseudo'],

				interpolation: {
					escapeValue: false,
				},

				// Only the fallback locale is bundled for synchronous first
				// paint. Other locales are fetched on demand by the HTTP backend.
				resources: bundledResources,

				backend: {
					// Pseudo (`en-XA`) has no JSON files on disk — the
					// `i18next-pseudo` post-processor wraps the loaded
					// resources at render time. Alias en-XA requests to
					// the en-US JSON so the backend fetches real content
					// instead of 404-ing on every namespace.
					loadPath: (
						lngs: ReadonlyArray<string>,
						namespaces: ReadonlyArray<string>,
					) => {
						const requested = lngs[0];
						const resolved =
							requested === PSEUDO_LANGUAGE_TAG
								? FALLBACK_LANGUAGE
								: requested;

						return `/locales/${resolved}/${namespaces[0]}.json`;
					},
				},

				react: {
					useSuspense: true,
				},

				// partialBundledLanguages allows additional namespaces to be
				// loaded on demand without requiring them to be declared upfront
				// in the ns array. Only the shell namespace is preloaded.
				partialBundledLanguages: true,
			});

		i18nDebugLog('i18n initialized successfully');
		i18nDebugLog('current language:', i18n.language);
		i18nDebugLog('loaded namespaces:', i18n.options.ns);
	} catch (error) {
		// Always surface init failures — a half-initialised i18n instance
		// renders every key as the key itself, which is worse than crashing
		// fast and letting the shell error boundary recover.
		i18nDebugError('failed to initialize i18next:', error);
		throw error;
	}

	return i18n;
};

export {
	detectLanguage,
	initI18n,
	isLanguageConfigurationLocked,
	resolveSupportedLanguage,
};
export default i18n;
